import { DateFormat } from '@startuptools/ui/DateFormat';
import { Button } from '@startuptools/ui/Button';
import { Progress, ProgressStep } from '@startuptools/ui/Progress';
import { VestingProgress } from '@startuptools/ui/VestingProgress';
import { Link } from '@startuptools/ui/Link';
import { Typography } from '@startuptools/ui/Typography';
import { Stack } from '@startuptools/ui/Stack';
import { Trans, useLingui } from '@lingui/react';
import { type GqlWarrantProgram } from '../../../../graphql/base-types.graphql';
import {
  agreementStatusToProgress,
  AgreementStatusExplanation,
  DecisionStatusExplanation,
  decisionStatusToProgress,
  registrationStatusToProgress,
  RegistrationStatusExplanation,
} from './utils';
import React from 'react';
import { DateTime } from 'luxon';
import { DashboardTable, EmptyState } from './Table';
import { useNavigate } from '../../../../components/Navigate';
import { Link as RouterLink } from 'react-router-dom';
import { wpEditStepProgramLink } from '../../../../components/Links';

export const WarrantProgramTable = ({
  warrantPrograms,
}: {
  warrantPrograms: Pick<
    GqlWarrantProgram,
    | 'id'
    | 'name'
    | 'warrantKind'
    | 'warrantStartDate'
    | 'agreementStatus'
    | 'registrationStatus'
    | 'decisionStatus'
    | 'warrantEndDate'
    | 'description'
    | 'usesStepper'
  >[];
}) => {
  const { link } = useNavigate();
  const { i18n } = useLingui();

  const hasWarrantPrograms = warrantPrograms.length > 0;
  return (
    <DashboardTable
      title={<Trans id="Option programs" />}
      dateLabel={<Trans id="Start date" />}
      hasItems={hasWarrantPrograms}
      hoverRow={false}
      rows={warrantPrograms.map(wp => (
        <React.Fragment key={wp.id}>
          <tr>
            <td>
              <Link fontWeight={600} component={RouterLink} to={link(wpEditStepProgramLink(wp))}>
                <Stack gap={1}>
                  <Typography level="body-md" noWrap>
                    {wp.name ?? wp.description}
                  </Typography>
                  {!wp.name && (
                    <Typography level="body-sm" textColor="neutral.500" noWrap>
                      {wp.description}
                    </Typography>
                  )}
                </Stack>
              </Link>
            </td>
            <td>
              <DateFormat locale={i18n.locale}>{wp.warrantStartDate}</DateFormat>
            </td>
            <td>
              <Progress>
                <ProgressStep
                  status={agreementStatusToProgress(wp.agreementStatus)}
                  label={<Trans id="Agreement" />}
                  tooltipText={<AgreementStatusExplanation status={wp.agreementStatus} />}
                />
                <ProgressStep
                  status={decisionStatusToProgress(wp.decisionStatus)}
                  label={<Trans id="Decision" />}
                  tooltipText={<DecisionStatusExplanation status={wp.decisionStatus} />}
                />
                <ProgressStep
                  status={registrationStatusToProgress(wp.registrationStatus)}
                  tooltipText={<RegistrationStatusExplanation status={wp.registrationStatus} />}
                  label={<Trans id="Registration" />}
                />
              </Progress>
            </td>
          </tr>

          <tr>
            {wp.warrantStartDate && wp.warrantEndDate && (
              <td colSpan={3}>
                <VestingProgress
                  locale={i18n.locale}
                  start={DateTime.fromISO(wp.warrantStartDate)}
                  end={DateTime.fromISO(wp.warrantEndDate)}
                />
              </td>
            )}
          </tr>
        </React.Fragment>
      ))}
      emptyState={
        <EmptyState
          header={<Trans id="You haven't created any Option Programs yet. Get started by creating your first one." />}
          cta={
            <Button size="sm" component={RouterLink} to={link('/stock-options/employee-options')}>
              <Trans id="Create option program" />
            </Button>
          }
        />
      }
    />
  );
};
